import { API } from "@/config/apiBase";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/auth/password-reset/request`, { email });
      setIsSubmitted(true);
    } catch (error) {
      // API always returns 200 to prevent enumeration, but handle network errors
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-8">
        <motion.div 
          className="w-full max-w-md text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-[#F4F1EA]">
            <div className="w-16 h-16 bg-[#81B29A]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-[#81B29A]" />
            </div>
            
            <h1 className="text-2xl font-bold text-[#3D405B] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
              Check Your Email
            </h1>
            
            <p className="text-[#6D6F7C] mb-6">
              If an account exists for <span className="font-medium text-[#3D405B]">{email}</span>, we've sent a password reset link. It'll expire in 60 minutes.
            </p>
            
            <p className="text-sm text-[#9CA3AF] mb-6">
              Didn't receive the email? Check your spam folder or try again.
            </p>
            
            <div className="space-y-3">
              <Button
                onClick={() => setIsSubmitted(false)}
                variant="outline"
                className="w-full border-2 border-[#F4F1EA] hover:bg-[#F4F1EA] rounded-full h-12 text-[#3D405B]"
              >
                Try another email
              </Button>
              
              <Link to="/login" className="block">
                <Button
                  className="w-full bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full h-12"
                >
                  Back to Login
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-8">
      <motion.div 
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Link to="/login" className="inline-flex items-center text-[#6D6F7C] hover:text-[#3D405B] mb-8 transition-colors" data-testid="back-link">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to login
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-[#E07A5F] rounded-full flex items-center justify-center">
            <Phone className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
            Forgot Password?
          </h1>
        </div>
        
        <p className="text-[#6D6F7C] mb-8">
          No worries! Enter your email and we'll send you a link to reset your password.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-[#3D405B]">Email</Label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-12 bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl h-14 text-[#3D405B]"
                placeholder="your@email.com"
                required
                data-testid="email-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full h-14 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
            data-testid="submit-button"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                Sending...
              </span>
            ) : (
              "Send Reset Link"
            )}
          </Button>
        </form>

        <p className="text-center text-[#6D6F7C] mt-8">
          Remember your password?{" "}
          <Link to="/login" className="text-[#E07A5F] font-semibold hover:underline" data-testid="login-link">
            Sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
